import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

/**
 * Интерфейс контекста для управления видимостью столбцов таблицы
 */
interface TableColumnsContextProps {
  /** Массив ключей видимых столбцов */
  visibleColumns: string[];
  /** Функция для установки видимых столбцов */
  setVisibleColumns: (columns: string[]) => void;
  /** Функция для переключения видимости столбца по ключу */
  toggleColumnVisibility: (columnKey: string) => void;
  /** Массив всех доступных столбцов с их метаданными */
  allColumns: { key: string; header: string; field: string }[];
  /** Функция для установки всех доступных столбцов */
  setAllColumns: (columns: { key: string; header: string; field: string }[]) => void;
}

const TableColumnsContext = createContext<TableColumnsContextProps | undefined>(undefined);

/**
 * Пропсы для провайдера контекста TableColumnsProvider
 */
interface TableColumnsProviderProps {
  /** Дочерние компоненты */
  children: ReactNode;
  /** Ключ для хранения настроек видимости столбцов в localStorage */
  storageKey: string;
  /** Массив ключей столбцов, которые должны быть видимы по умолчанию */
  defaultColumns: string[];
  /** Массив всех доступных столбцов с их метаданными */
  columns: { key: string; header: string; field: string }[];
}

/**
 * Провайдер контекста для управления видимостью столбцов таблицы
 *
 * @example
 * ```tsx
 * <TableColumnsProvider
 *   storageKey="my-table-columns"
 *   defaultColumns={['name', 'email']}
 *   columns={[
 *     { key: 'name', header: 'Name', field: 'name' },
 *     { key: 'email', header: 'Email', field: 'email' },
 *     { key: 'phone', header: 'Phone', field: 'phone' },
 *   ]}
 * >
 *   <MyTable />
 * </TableColumnsProvider>
 * ```
 */
export const TableColumnsProvider: React.FC<TableColumnsProviderProps> = ({
  children,
  storageKey,
  defaultColumns,
  columns,
}) => {
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [allColumns, setAllColumns] = useState(columns);

  // Загружаем сохраненные столбцы при монтировании
  useEffect(() => {
    const savedColumns = localStorage.getItem(storageKey);

    if (!savedColumns || savedColumns === '[]' || savedColumns === 'null') {
      localStorage.setItem(storageKey, JSON.stringify(defaultColumns));
      setVisibleColumns(defaultColumns);
    } else {
      try {
        const parsed = JSON.parse(savedColumns);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setVisibleColumns(parsed);
        } else {
          setVisibleColumns(defaultColumns);
        }
      } catch (e) {
        setVisibleColumns(defaultColumns);
      }
    }
  }, [storageKey, defaultColumns]);

  // Обновляем allColumns при изменении входных columns
  useEffect(() => {
    setAllColumns(columns);
  }, [columns]);

  // Функция для переключения видимости столбца
  const toggleColumnVisibility = (columnKey: string) => {
    const newVisibleColumns = visibleColumns.includes(columnKey)
      ? visibleColumns.filter(key => key !== columnKey)
      : [...visibleColumns, columnKey];

    // Убедимся, что хотя бы один столбец всегда видим
    if (newVisibleColumns.length === 0) {
      return;
    }

    setVisibleColumns(newVisibleColumns);
    localStorage.setItem(storageKey, JSON.stringify(newVisibleColumns));
  };

  return (
    <TableColumnsContext.Provider
      value={{
        visibleColumns,
        setVisibleColumns,
        toggleColumnVisibility,
        allColumns,
        setAllColumns,
      }}
    >
      {children}
    </TableColumnsContext.Provider>
  );
};

/**
 * Хук для доступа к контексту управления видимостью столбцов таблицы
 *
 * @returns {TableColumnsContextProps} Объект с методами и свойствами для управления видимостью столбцов
 *
 * @example
 * ```tsx
 * function MyTable() {
 *   const { visibleColumns, toggleColumnVisibility } = useTableColumns();
 *
 *   // Фильтруем столбцы на основе visibleColumns
 *   const filteredColumns = useMemo(() => {
 *     return columns.filter(col => visibleColumns.includes(col.key));
 *   }, [columns, visibleColumns]);
 *
 *   return <Table columns={filteredColumns} data={data} />;
 * }
 * ```
 */
export const useTableColumns = () => {
  const context = useContext(TableColumnsContext);
  if (context === undefined) {
    throw new Error('useTableColumns must be used within a TableColumnsProvider');
  }
  return context;
};
